import * as React from 'react';
import cx from 'classnames';
import { DatasheetItem } from '@modules/datasheets/components/datasheet-item';
import { DatasheetItemLabel } from '@modules/datasheets/components/datasheet-item-label';
import { MovableItem } from '../movable-item';
import { TextFieldProps, useTextFieldController } from './text-field.controller';
import { Flex } from '@common/components/Flex';
import { Hint } from '@common/components/hint';
import { DatasheetFieldComponentHeadingPosition } from '@common/dto/datasheet/field-component/heading-position';
import { DatasheetFieldComponentTextAlign } from '@common/dto/datasheet/field-component/text-align';
import { TabOrder } from '../tab-order';
import { NON_TAB_FIELD_TYPES } from '@modules/datasheets/utils/tab-navigation';
import { FieldType } from '@common/dto/field/type';

import './text-field.styles.scss';

export const TextField: React.FC<TextFieldProps> = React.memo((props) => {
  const { datasheetElement, multipleElementsSelected, isTabOrderSettingActive } = props;

  const {
    coordinates,
    datasheetElementSelected,
    handleDatasheetElementMouseDown,
    movableElementRef,
    handleResizing,
    size,
    headingReference,
    headingPlacedHorizontally,
    headingSize,
    headingPosition,
    handleTabOrderChange,
  } = useTextFieldController(props);


  const displayText = datasheetElement.original.headdisplay
    ? (datasheetElement.original.headtext && datasheetElement.original.headtext.trim() !== '' 
        ? datasheetElement.original.headtext 
        : (datasheetElement.original.headingpds && datasheetElement.original.headingpds !== "" 
            ? datasheetElement.original.headingpds 
            : datasheetElement.original.field.fieldname))
    : undefined;


  const imageURL = datasheetElement.original.field?.imageURL || datasheetElement.original.field?.fieldtype?.imageURL || '';
  const shouldShowImage = datasheetElement.original.showfieldtype && Boolean(imageURL);

  const fieldWidth = size.width;
  const fieldHeight = size.height;
  const isEditable = datasheetElement.original.editable !== false;

  const originField = datasheetElement.original.field;
  const resolvedFieldType: number =
    (originField as any)?.fieldtype?.typeid ?? (originField as any)?.type ?? (originField as any)?.rawFieldType ?? -1;
  const isNonTabEligible = NON_TAB_FIELD_TYPES.has(resolvedFieldType as FieldType);

  // Horizontal gap between heading text and the field box for LEFT/RIGHT positions,
  // similar to padding used in the legacy GWT implementation.
  const HEADING_SIDE_GAP = 5;

  const isHeadingTopOrBottom = [
    DatasheetFieldComponentHeadingPosition.top,
    DatasheetFieldComponentHeadingPosition.bottom,
  ].includes(headingPosition);

  const headingAlignmentStyle = isHeadingTopOrBottom
    ? {
        width: fieldWidth,
        left: 0,
        ...(datasheetElement.original.headalign === DatasheetFieldComponentTextAlign.left
          ? { justifyContent: 'flex-start' }
          : datasheetElement.original.headalign === DatasheetFieldComponentTextAlign.center
          ? { justifyContent: 'center' }
          : { justifyContent: 'flex-end' }),
      }
    : undefined;

  // Position the heading around the field without moving the field's anchor point,
  // mimicking the classic GWT DatasheetFieldLabel behavior.
  const headingPositionOffset =
    headingPosition === DatasheetFieldComponentHeadingPosition.top
      ? { top: -headingSize.height, left: 0 }
      : headingPosition === DatasheetFieldComponentHeadingPosition.bottom
      ? { top: fieldHeight, left: 0 }
      : headingPosition === DatasheetFieldComponentHeadingPosition.left
      ? {
          // GWT: for LEFT/RIGHT, only ALIGN_CENTER is vertically centered;
          // other align values keep the label anchored to the top of the field.
          top:
            datasheetElement.original.headalign === DatasheetFieldComponentTextAlign.center
              ? (fieldHeight - headingSize.height) / 2
              : 0,
          left: -headingSize.width - HEADING_SIDE_GAP,
        }
      : headingPosition === DatasheetFieldComponentHeadingPosition.right
      ? {
          top:
            datasheetElement.original.headalign === DatasheetFieldComponentTextAlign.center
              ? (fieldHeight - headingSize.height) / 2
              : 0,
          left: fieldWidth + HEADING_SIDE_GAP,
        }
      : undefined;

  const labelComponent = (
    <DatasheetItemLabel
      reference={headingReference}
      inline={headingPlacedHorizontally}
      withImg={shouldShowImage}
      withText={datasheetElement.original.headdisplay}
      text={displayText ?? ''}
      img={imageURL}
      textCustomization={{
        fontSize: datasheetElement.original.headfontsize,
        fontFamily: datasheetElement.original.headfont,
        color: datasheetElement.original.headcolor,
        align: datasheetElement.original.headalign,
        fontStyles: datasheetElement.original.headstyle,
      }}
      // Always position the heading absolutely relative to the field container
      // so changing headpos/headalign does not shift the field itself.
      absolutePosition={true}
      alignmentStyle={isHeadingTopOrBottom ? headingAlignmentStyle : undefined}
      positionOffset={headingPositionOffset}
    />
  );

  return (
    <DatasheetItem
      onMouseDown={isTabOrderSettingActive ? undefined : handleDatasheetElementMouseDown}
      style={{
        top: coordinates.y,
        left: coordinates.x,
      }}
    >
      {labelComponent}
      <Flex>
        <MovableItem
          resizing={{
            enabled: isTabOrderSettingActive ? false : !multipleElementsSelected,
            onChange: handleResizing,
          }}
          moving={{
            enabled: !isTabOrderSettingActive,
          }}
          onClick={isTabOrderSettingActive && !datasheetElement.original.field?.system && !isNonTabEligible
            ? handleTabOrderChange
            : undefined}
          showBorder={datasheetElement.original.showborder}
          className={cx(
            "DatasheetsEditorDatasheetTextField__field",
            { "DatasheetsEditorDatasheetTextField__field--editable": isEditable },
            { "DatasheetsEditorDatasheetTextField__field--system": Boolean(datasheetElement.original.field?.system) }
          )}
          reference={movableElementRef}
          height={size.height}
          width={size.width}
          selected={datasheetElementSelected}
          onMouseDown={isTabOrderSettingActive ? undefined : handleDatasheetElementMouseDown}
        />
        {isTabOrderSettingActive && isEditable
          && !datasheetElement.original.field?.system
          && !isNonTabEligible
          && datasheetElement.original.tabOrder > 0 && (
          <TabOrder
            value={datasheetElement.original.tabOrder}
            onClick={handleTabOrderChange}
          />
        )}
        {!isTabOrderSettingActive && datasheetElement.original.helptext && (
          <Hint className="DatasheetsEditorDatasheetTextField__hint" description={datasheetElement.original.helptext} />
        )}
      </Flex>
    </DatasheetItem>
  );
});